import type { WebwinImage } from "../../engine/ImageStore.js";

/**
 * One node of a tree, as data.
 *
 * Trees are described declaratively and the control derives its rows from the
 * description, so an application mutates plain objects and never assembles a
 * parallel control hierarchy.
 */
export interface TreeNodeSpec<T = unknown> {
  text: string;
  icon?: WebwinImage | null;
  children?: TreeNodeSpec<T>[];
  /** Whether the node is open. The control writes this back as you expand. */
  expanded?: boolean;
  /** Tri-state is not supported; a node is checked or it is not. */
  checked?: boolean;
  enabled?: boolean;
  tag?: T;
  /**
   * Produce the children the first time this node opens.
   *
   * Lets a large tree — a file system, a scene graph — stay unbuilt until it
   * is looked at. A node with a loader is drawn as expandable before anything
   * has been loaded; returning an empty list turns the expander off.
   */
  loadChildren?: (node: TreeNodeSpec<T>) => TreeNodeSpec<T>[];
}

/** A node flattened into a drawable row. */
export interface TreeRow<T = unknown> {
  node: TreeNodeSpec<T>;
  /** 0 for a root node. */
  depth: number;
  parent: TreeNodeSpec<T> | null;
  /** True when the node can be opened, loader included. */
  expandable: boolean;
  /** True when this is the last child of its parent — used for the lines. */
  last: boolean;
  /**
   * For each ancestor level, whether a vertical line continues past this row.
   * Index 0 is the outermost level.
   */
  guides: boolean[];
}

export function nodeChildren<T>(node: TreeNodeSpec<T>): TreeNodeSpec<T>[] {
  return node.children ?? [];
}

export function isExpandable<T>(node: TreeNodeSpec<T>): boolean {
  if (nodeChildren(node).length > 0) return true;
  // A loader counts only until it has run. Once it has and produced nothing,
  // the node is a leaf and must stop advertising an expander.
  return node.loadChildren !== undefined && node.children === undefined;
}

export function isNodeEnabled<T>(node: TreeNodeSpec<T>): boolean {
  return node.enabled !== false;
}

/**
 * Walk the tree and produce the rows that are currently visible.
 *
 * Only open branches contribute, so this is the list the control scrolls,
 * paints, hit-tests and navigates — the flattening is the whole trick that
 * lets a tree reuse a flat virtualized list underneath.
 */
export function flattenTree<T>(roots: readonly TreeNodeSpec<T>[]): TreeRow<T>[] {
  const rows: TreeRow<T>[] = [];

  const walk = (
    nodes: readonly TreeNodeSpec<T>[],
    depth: number,
    parent: TreeNodeSpec<T> | null,
    guides: boolean[],
  ): void => {
    nodes.forEach((node, i) => {
      const last = i === nodes.length - 1;
      rows.push({ node, depth, parent, expandable: isExpandable(node), last, guides: [...guides] });
      if (node.expanded) {
        // A line continues down past this row only if it has later siblings.
        walk(nodeChildren(node), depth + 1, node, [...guides, !last]);
      }
    });
  };

  walk(roots, 0, null, []);
  return rows;
}

/** Depth-first search for the row holding `node`, or -1. */
export function rowIndexOf<T>(rows: readonly TreeRow<T>[], node: TreeNodeSpec<T> | null): number {
  if (!node) return -1;
  return rows.findIndex((r) => r.node === node);
}

/** Every node in the tree, open or not. */
export function walkAll<T>(
  nodes: readonly TreeNodeSpec<T>[],
  visit: (node: TreeNodeSpec<T>, depth: number) => void,
  depth = 0,
): void {
  for (const node of nodes) {
    visit(node, depth);
    walkAll(nodeChildren(node), visit, depth + 1);
  }
}

/** Where a row sits among its siblings, for a reader that announces "3 of 7". */
export interface TreeItemPosition {
  /** 0 for a root node, matching `TreeRow.depth`. */
  level: number;
  /** 0-based among siblings. */
  posInSet: number;
  setSize: number;
}

/**
 * Sibling positions for a flattened tree, one per row.
 *
 * `flattenTree` gives depth and `last`, which is everything the lines need and
 * not what a screen reader asks for: ARIA wants position among *siblings*,
 * while the flat list only knows position among rows. Announcing the latter
 * tells someone the second child of the third folder is "item 7 of 40", which
 * is a sentence about a data structure rather than about their tree.
 *
 * Counting from the visible rows is enough. A node is only ever in the list
 * when its parent is open, and an open parent contributes all of its children.
 */
export function treeItemPositions<T>(rows: readonly TreeRow<T>[]): TreeItemPosition[] {
  const total = new Map<TreeNodeSpec<T> | null, number>();
  for (const row of rows) total.set(row.parent, (total.get(row.parent) ?? 0) + 1);

  const seen = new Map<TreeNodeSpec<T> | null, number>();
  return rows.map((row) => {
    const posInSet = seen.get(row.parent) ?? 0;
    seen.set(row.parent, posInSet + 1);
    return { level: row.depth, posInSet, setSize: total.get(row.parent) ?? 1 };
  });
}
